'use client';

import React, { useCallback, useState } from 'react';
import { Heart } from 'lucide-react';
import { useFavorites } from '@/lib/hook/useFavorites';
import { useToast } from '@/components/ui/use-toast';
import { cn } from '@/lib/utils';

interface FavoriteButtonProps {
  productId: number;
  productTitle?: string;
  productUrl?: string;
  productImageUrl?: string;
  className?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'overlay' | 'inline';
}

const sizeMap = {
  sm: 'w-8 h-8',
  md: 'w-10 h-10',
  lg: 'w-12 h-12',
};

const iconSizeMap = {
  sm: 'w-4 h-4',
  md: 'w-5 h-5',
  lg: 'w-6 h-6',
};

export default function FavoriteButton({
  productId,
  productTitle = '',
  productUrl = '',
  productImageUrl = '',
  className = '',
  size = 'sm',
  variant = 'overlay',
}: FavoriteButtonProps) {
  const { isFavorite, toggle } = useFavorites();
  const { toast } = useToast();
  const [isAnimating, setIsAnimating] = useState(false);
  const [hasError, setHasError] = useState(false);

  const favorited = isFavorite(productId);

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      e.stopPropagation();

      setHasError(false);
      setIsAnimating(true);

      const result = toggle({
        id: productId,
        title: productTitle,
        url: productUrl,
        imageUrl: productImageUrl,
      });

      setTimeout(() => setIsAnimating(false), 300);

      if (!result.added && result.undo) {
        toast({
          description: (
            <div className="flex items-center justify-between w-full">
              <span>Favorilerden kaldırıldı</span>
              <button
                onClick={() => {
                  result.undo?.();
                }}
                className="ml-4 font-semibold text-hzc_purple_500 hover:underline"
              >
                Geri al
              </button>
            </div>
          ),
          duration: 5000,
        });
      }
    },
    [productId, productTitle, productUrl, productImageUrl, toggle, toast],
  );

  return (
    <button
      onClick={handleClick}
      aria-label={favorited ? 'Favorilerden kaldır' : 'Favorilere ekle'}
      className={cn(
        'flex items-center justify-center rounded-full transition-all duration-150 ease-in-out',
        variant === 'overlay'
          ? 'bg-white/90 hover:bg-white shadow-[0_1px_4px_rgba(0,0,0,0.12)] border border-[#E5E7EB] backdrop-blur-sm'
          : 'bg-white/90 hover:bg-white shadow-[0_1px_4px_rgba(0,0,0,0.12)] border border-[#E5E7EB]',
        sizeMap[size],
        hasError && 'animate-[shake_0.3s_ease-in-out]',
        className,
      )}
    >
      <Heart
        className={cn(
          iconSizeMap[size],
          'transition-all duration-150 ease-in-out',
          favorited
            ? 'fill-[#E84393] text-[#E84393]'
            : 'fill-transparent text-[#555B99]',
          isAnimating && 'scale-125',
        )}
      />
    </button>
  );
}
